import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as groupsKeys, useAddRolesToGroupMutation } from '~/queries/user-groups'
import { useRolesListQuery } from '~/queries/user-roles'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'add-role-to-group-modal'

export interface FormValues {
  relatedRoles: IOption[]
}

const validationSchema = Yup.object().shape({
  relatedRoles: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED).min(1, FORM_ERRORS.AT_LEAST_ONE),
})

interface FormProps {
  groupId: string
  onClose: VoidFunction
  onFailure: (error: unknown) => void
  onSuccess: VoidFunction
}

const AddRoleToGroupForm = ({ groupId, onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: { relatedRoles: [] },
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { mutateAsync: addRolesToGroup } = useAddRolesToGroupMutation()

  const { data: roles, isLoading: isRolesLoading } = useRolesListQuery()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { relatedRoles } = values

    try {
      const roleIds = relatedRoles.map(({ value }) => value)
      await addRolesToGroup({ groupId, roleIds })
      await queryClient.invalidateQueries({ queryKey: groupsKeys.details(groupId) })
      onSuccess()
    } catch (error) {
      onFailure(error)
    }
  }

  const options = roles?.data.map(({ name, role_id }) => ({ label: name, value: role_id })) || []

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="relatedRoles"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                autoFocus
                error={errors.relatedRoles?.message}
                isLoading={isRolesLoading}
                label="Roles"
                options={options}
                placeholder="Select roles"
                required
              />
            )}
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const AddRoleToGroupModal = ({ groupId, isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add roles to the group</ModalHeader>
      <AddRoleToGroupForm groupId={groupId} onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default AddRoleToGroupModal
